import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ModuleNav } from "./ModuleNav";
import { Download, Search, Trash2, Clock, ShieldAlert } from "lucide-react";

const EMPTY_BATCH = { method: "", witness: "", note: "", override_reason: "" };

const Stat = ({ label, value, sub, tone = "", active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`border p-3 text-left transition-colors ${active
            ? "border-[#134e4a] bg-teal-50" : "border-slate-200 bg-white hover:border-[#134e4a]"}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num mt-0.5 text-2xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </button>
);

export default function Retention() {
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ item_type: "block", flag: "eligible", q: "" });
  const [selected, setSelected] = useState([]);
  const [batch, setBatch] = useState({ ...EMPTY_BATCH });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const [list, sum] = await Promise.all([
        api.get("/retention/worklist", { params: p }),
        api.get("/retention/summary"),
      ]);
      setRows(list.data);
      setSummary(sum.data);
      setSelected([]);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/retention/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const canDispose = !!meta?.can_dispose;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = rows.length > 0 && selected.length === rows.length;

  const dispose = async () => {
    if (!batch.method) return toast.error("Pick a disposal method");
    setBusy("dispose");
    try {
      const { data } = await api.post("/retention/dispose", {
        item_type: filters.item_type, ids: selected, method: batch.method,
        witness: batch.witness, note: batch.note, override_reason: batch.override_reason,
      });
      toast.success(`${data.disposed} item(s) recorded as disposed`);
      if (data.skipped?.length) {
        toast.error(`${data.skipped.length} skipped — ${data.skipped[0].reason}`);
      }
      setOpen(false);
      setBatch({ ...EMPTY_BATCH });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/retention/export.csv", {
        params: { item_type: filters.item_type, flag: filters.flag }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PathQity_Retention.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="retention-page">
      <ModuleNav
        title="Retention & Disposal"
        blurb="Retention clocks on every block, slide, report and wet-tissue container — and a disposal worklist with a signed record of what left the lab."
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} data-testid="retention-export-button" className="h-11 border-slate-300">
              <Download size={15} className="mr-2" /> Export CSV
            </Button>
            {canDispose && (
              <Button onClick={() => setOpen(true)} disabled={selected.length === 0}
                      data-testid="retention-dispose-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Trash2 size={15} className="mr-2" /> Record disposal ({selected.length})
              </Button>
            )}
          </>
        }
      />

      {summary && (
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="retention-metrics">
          {summary.categories.map((c) => (
            <Stat key={c.item_type} label={c.label} value={c.eligible}
                  sub={`${c.eligible} eligible now · ${c.retained} retained · ${c.disposed} disposed`}
                  tone={c.eligible ? "text-amber-700" : "text-[#134e4a]"}
                  active={filters.item_type === c.item_type}
                  testid={`retention-tile-${c.item_type}`}
                  onClick={() => setFilters((f) => ({ ...f, item_type: c.item_type }))} />
          ))}
        </section>
      )}

      {summary && (
        <p className="border-l-4 border-[#134e4a] bg-teal-50 px-3 py-2 text-xs text-slate-700" data-testid="retention-rules-note">
          Blocks {summary.rules.block_years} yrs · surgical slides {summary.rules.slide_surgical_years} yrs ·
          cytology slides {summary.rules.slide_cytology_years} yrs · FNA slides {summary.rules.slide_fna_years} yrs ·
          reports {summary.rules.report_years} yrs · wet tissue {summary.rules.wet_tissue_days} days after sign-out.
          Change these in <Link to="/team" className="font-semibold underline">Team &amp; Lab Setup</Link>.
          Nothing is ever deleted automatically — items simply become eligible.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="retention-filters">
        <div className="relative w-full sm:w-60">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                 placeholder="Accession, patient, MRN" data-testid="retention-search" className="h-10 pl-9" />
        </div>
        <select value={filters.item_type} data-testid="retention-type-filter"
                onChange={(e) => setFilters((f) => ({ ...f, item_type: e.target.value }))}
                className="h-10 border border-slate-300 bg-white px-2 text-sm">
          {(meta?.item_types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {[["eligible", "Eligible now"], ["due_soon", "Due within 90 days"], ["retained", "Still retained"],
          ["disposed", "Disposed"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilters((f) => ({ ...f, flag: k }))}
                  data-testid={`retention-filter-${k}`}
                  className={`h-10 border px-3 text-sm font-medium ${filters.flag === k
                    ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
            {label}
          </button>
        ))}
      </div>

      <section className="border border-slate-200" data-testid="retention-worklist">
        {rows.length === 0 ? (
          <p className="px-3 py-10 text-center text-sm text-slate-500" data-testid="retention-empty">
            Nothing in this bucket. Everything is either still within its retention window or already disposed.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2">
                    {canDispose && filters.flag !== "disposed" && (
                      <input type="checkbox" checked={allSelected} data-testid="retention-select-all"
                             onChange={() => setSelected(allSelected ? [] : rows.map((r) => r.id))}
                             className="h-4 w-4 accent-[#134e4a]" />
                    )}
                  </th>
                  <th className="px-3 py-2">Item</th><th className="px-3 py-2">Case</th>
                  <th className="px-3 py-2">Location</th><th className="px-3 py-2">Rule</th>
                  <th className="px-3 py-2">Eligible on</th><th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50"
                      data-testid={`retention-row-${r.id}`}>
                    <td className="px-3 py-2">
                      {canDispose && !r.disposed && (
                        <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)}
                               data-testid={`retention-check-${r.id}`} className="h-4 w-4 accent-[#134e4a]" />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs font-semibold text-slate-800">
                        {r.barcode || r.designation || r.item_label}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {r.item_label}{r.stain ? ` · ${r.stain}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/cases/${r.case_id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">
                        {r.accession_number}
                      </Link>
                      <div className="text-[11px] text-slate-500">{r.patient_name}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.location || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.rule}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {fmtDate(r.eligible_on)}
                      {!r.disposed && r.days_until_eligible !== null && (
                        <div className={`text-[11px] font-semibold ${r.eligible ? "text-amber-800" : "text-slate-400"}`}>
                          {r.eligible ? `${Math.abs(r.days_until_eligible)}d past` : `in ${r.days_until_eligible}d`}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.disposed ? (
                        <>
                          <span className="bg-slate-800 px-1.5 py-0.5 text-[11px] font-semibold text-white">Disposed</span>
                          <div className="text-[11px] text-slate-500">
                            {r.disposal_method} · {fmtDateTime(r.disposed_at)}
                            {r.disposed_by ? ` · ${r.disposed_by}` : ""}
                            {r.witness ? ` · witness ${r.witness}` : ""}
                          </div>
                        </>
                      ) : r.eligible ? (
                        <span className="inline-flex items-center gap-1 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                          <Clock size={11} /> Eligible
                        </span>
                      ) : (
                        <span className="bg-[#134e4a] px-1.5 py-0.5 text-[11px] font-semibold text-white">Retained</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="retention-dispose-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Record disposal — {selected.length} item(s)</DialogTitle>
            <DialogDescription>
              This writes a permanent, audited disposal record. The items stay in the inventory marked
              disposed so the inspector can see what left the lab and how.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Method</Label>
              <select value={batch.method} data-testid="dispose-method"
                      onChange={(e) => setBatch((b) => ({ ...b, method: e.target.value }))}
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Select a method…</option>
                {(meta?.methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Witness</Label>
              <Input value={batch.witness} data-testid="dispose-witness"
                     onChange={(e) => setBatch((b) => ({ ...b, witness: e.target.value }))}
                     placeholder="Second person who observed the disposal" className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Batch note</Label>
              <Textarea value={batch.note} data-testid="dispose-note"
                        onChange={(e) => setBatch((b) => ({ ...b, note: e.target.value }))}
                        className="mt-1 min-h-[60px] border-slate-300" />
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs uppercase tracking-wide text-slate-600">
                <ShieldAlert size={13} className="text-amber-700" /> Early-disposal justification
              </Label>
              <Input value={batch.override_reason} data-testid="dispose-override"
                     onChange={(e) => setBatch((b) => ({ ...b, override_reason: e.target.value }))}
                     placeholder="Only needed for items still inside the retention window"
                     className="mt-1 h-11 border-slate-300" />
            </div>
            <Button onClick={dispose} disabled={busy === "dispose"} data-testid="dispose-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "dispose" ? "Recording…" : "Record disposal"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
